import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment-timezone'
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { ShimmerTable } from 'react-shimmer-effects'
import {
  useErrorService,
  useUserService,
  useNotificationService,
} from '@abroad/components'
import { useIntentionReducer } from '../../components'
import { PageHeader } from '../common'
import API from '../../utils/API'
import ScheduleGuidelineModal from './ScheduleGuidelineModal'
import ConditionalScheduleGuidelineModal from './ConditionalScheduleGuidelineModal'
import CancelSessionModal from './CancelSessionModal'
import { QuestSessionSchedulingCalendar } from './index'
import { getSortedTimezones } from '../../utils/timezone'
import {
  gtmCancelSessionAction,
  gtmScheduleSessionAction,
  gtmScheduleMoreIndividualSessionAction,
} from '../quests/gtmfunctions'

const UpcomingSessions = ({ onCalendarViewChange }) => {
  const history = useHistory()
  const { user } = useUserService()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const errorServiceRef = useRef(Error)

  useEffect(() => {
    errorServiceRef.current = Error
  }, [Error])
  const { dispatch } = useIntentionReducer()
  const [isLoading, setIsLoading] = useState(true)
  const [toBeScheduledSessions, setToBeScheduledSessions] = useState([])
  const [scheduledSessions, setScheduledSessions] = useState([])
  const [showScheduleGuideline, setShowScheduleGuideline] = useState(false)
  const [selectedCoachRow, setSelectedCoachRow] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [sessionPendingCancel, setSessionPendingCancel] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [
    showConditionalScheduleGuideline,
    setShowConditionalScheduleGuideline,
  ] = useState(false)
  const [selectedCoachRowForConditional, setSelectedCoachRowForConditional] =
    useState(null)
  const [selectedRowForCalendar, setSelectedRowForCalendar] = useState(null)

  const userId = user?._id

  // Get user's timezone for date/time display
  const userTimezone = useMemo(() => {
    // First, try to get from user object
    const timezone = user?.timezone

    // Second, try to detect browser timezone
    let browserTimezone = ''
    try {
      if (
        typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat &&
        Intl.DateTimeFormat().resolvedOptions
      ) {
        browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      }
    } catch (e) {
      browserTimezone = ''
    }

    // Try user timezone first, then browser timezone
    const detectedTimezone = timezone || browserTimezone

    // Validate that the timezone exists in the timezone list
    if (detectedTimezone) {
      const timezones = getSortedTimezones()
      const timezoneExists = timezones.some(
        (tz) => tz.value === detectedTimezone,
      )
      if (timezoneExists) {
        return detectedTimezone
      }
    }

    // Fallback to browser timezone or 'Asia/Kolkata'
    return browserTimezone || 'Asia/Kolkata'
  }, [user])

  const fetchSessions = useCallback(async () => {
    try {
      if (!userId) {
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      const { data } = await API.session.getScheduling(userId)
      if (data?.success) {
        setToBeScheduledSessions(data?.toBeScheduled || [])
        setScheduledSessions(data?.scheduled || [])
      }
      setIsLoading(false)
      dispatch({ type: 'POST_SESSION_SUCCESS', payload: null })
    } catch (e) {
      setIsLoading(false)
      if (e?.code === 'upgrade_plan') {
        dispatch({ type: 'POST_SESSION_FAIL', payload: e })
      } else {
        dispatch({ type: 'POST_SESSION_FAIL', payload: null })
        errorServiceRef.current.showError(e)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dispatch])

  useEffect(() => {
    fetchSessions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScheduleSuccess = () => {
    // Refresh sessions after successful scheduling
    fetchSessions()
    // Hide calendar and return to table view
    setSelectedRowForCalendar(null)
  }

  // Check if should use new conditional flow
  const shouldUseConditionalFlow = (coachRow) => {
    const totalSessions = coachRow?.totalSessions || 0
    const isConditionalSchedulingEnabled =
      coachRow?.isConditionalSchedulingEnabled || false
    return totalSessions <= 3 && isConditionalSchedulingEnabled === true
  }

  // Check if should use old flow
  const shouldUseOldFlow = (coachRow) => {
    const totalSessions = coachRow?.totalSessions || 0
    const isConditionalSchedulingEnabled =
      coachRow?.isConditionalSchedulingEnabled || false
    const sowType = coachRow?.sowType || coachRow?.sow?.sowType
    // Old flow: For QUEST/REGULAR types, if (totalSessions > 3) OR (totalSessions <= 3 && isConditionalSchedulingEnabled === false)
    if (sowType === 'QUEST' || sowType === 'REGULAR') {
      return (
        totalSessions > 3 ||
        (totalSessions <= 3 && isConditionalSchedulingEnabled === false)
      )
    }
    // For other types, use old flow by default if not conditional
    return !shouldUseConditionalFlow(coachRow)
  }

  const openScheduleModal = (coachRow) => {
    gtmScheduleSessionAction(
      getDescription(coachRow),
      user?._id,
      coachRow?.questId,
    )

    if (shouldUseConditionalFlow(coachRow)) {
      // New conditional flow - show ConditionalScheduleGuidelineModal first
      setSelectedCoachRowForConditional(coachRow)
      setShowConditionalScheduleGuideline(true)
    } else if (shouldUseOldFlow(coachRow)) {
      // Old flow - show guideline modal
      setSelectedCoachRow(coachRow)
      setShowScheduleGuideline(true)
    }
  }

  const closeScheduleModal = () => {
    setSelectedCoachRow(null)
    setShowScheduleGuideline(false)
  }

  const closeConditionalScheduleModal = () => {
    setSelectedCoachRowForConditional(null)
    setShowConditionalScheduleGuideline(false)
  }

  const handleConditionalScheduleAll = (coachRow) => {
    // Close the modal and show calendar UI
    closeConditionalScheduleModal()
    setSelectedRowForCalendar(coachRow)
  }

  const isFirstSessionScheduled = (coachRow) => {
    if (coachRow?.remainingSessions !== coachRow?.totalSessions) {
      return true
    }
    return false
    // if (!coachRow?.sowId) {
    //   return false
    // }
    // return scheduledSessions.some(
    //   (scheduled) => scheduled?.sowId === coachRow.sowId,
    // )
  }

  const handleScheduleRemaining = (coachRow) => {
    gtmScheduleMoreIndividualSessionAction(
      getDescription(coachRow),
      user?._id,
      coachRow?.questId,
    )

    // Check if should use conditional flow for remaining sessions
    const totalSessions = coachRow?.totalSessions || 0
    const isConditionalSchedulingEnabled =
      coachRow?.isConditionalSchedulingEnabled || false

    if (totalSessions <= 3 && isConditionalSchedulingEnabled === true) {
      // New conditional flow - show calendar directly (no modal for remaining)
      setSelectedRowForCalendar(coachRow)
    } else {
      // Use old flow - navigate to schedule-remaining page
      history.push('/coaching/sessions/schedule-remaining', {
        coachDetails: coachRow,
        useScheduleAll: true, // Use schedule-all flow
      })
    }
  }

  const handleReschedule = (scheduledRow) => {
    history.push('/coaching/sessions/reschedule', {
      scheduledSession: scheduledRow,
    })
  }

  const openCancelModal = (sessionRow) => {
    setSessionPendingCancel(sessionRow)
    setShowCancelModal(true)
  }

  const closeCancelModal = () => {
    setShowCancelModal(false)
    setSessionPendingCancel(null)
  }

  const handleCancelConfirm = async () => {
    if (!sessionPendingCancel) {
      return
    }

    gtmCancelSessionAction(
      getDescription(sessionPendingCancel),
      getTimeValue(sessionPendingCancel),
      user?._id,
      sessionPendingCancel?.questId,
    )

    const coachId = sessionPendingCancel?.coachId
    const eventId = sessionPendingCancel?.eventId || sessionPendingCancel?._id

    if (!coachId || !eventId) {
      Notification.showNotification(
        'Unable to find session details. Please try again later.',
        'danger',
      )
      return
    }

    try {
      setIsCancelling(true)
      const { data } = await API.session.cancelSession(coachId, eventId)

      if (!data?.success) {
        Notification.showNotification(
          data?.message ||
            'Unable to cancel the session. Please try again later.',
          'danger',
        )
        return
      }

      Notification.showNotification(
        data?.message || 'Session cancelled successfully.',
        'success',
      )
      closeCancelModal()
      await fetchSessions()
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsCancelling(false)
    }
  }

  const renderEmptyRow = (columns, text) => (
    <tr>
      <td className='text-center s8 py-3' colSpan={columns}>
        {text}
      </td>
    </tr>
  )

  const getFname = (person) => {
    const name = person?.fname
    return name && String(name).trim() ? String(name).trim() : '-'
  }
  const getFullName = (person) => {
    const name = person?.fullName
    return name && String(name).trim()
      ? String(name).trim()
      : person?.fullName || '-'
  }

  const getDescription = (row) => {
    const sowType = row?.sowType || row?.sow?.sowType
    const clientName = getFname(row?.client)
    const coachName = getFname(row?.coach)

    if (sowType === 'QUEST') {
      const formatQuestSowDescription = (sessionLabelPart) => {
        const label = (sessionLabelPart || '').trim()
        return label
          ? `${label}: ${coachName} & ${clientName}`
          : `Wisdom Sessions: ${coachName} & ${clientName}`
      }

      const remainingSessions = row?.remainingSessions
      const totalSessions = row?.totalSessions

      // If remainingSessions === totalSessions, show simple format
      if (
        remainingSessions !== undefined &&
        totalSessions !== undefined &&
        remainingSessions === totalSessions
      ) {
        return formatQuestSowDescription('')
      }

      // For scheduled sessions, use single sessionIndex from schedulingCondition
      if (row?.schedulingCondition?.sessionIndex) {
        const sessionIndex = row.schedulingCondition.sessionIndex
        let sessionLabel = ''
        if (sessionIndex === 1) {
          sessionLabel = 'Preparation Session'
        } else if (sessionIndex === 2) {
          sessionLabel = ' Integration Session'
        } else if (sessionIndex === 3) {
          sessionLabel = 'Experience Session'
        } else if (sessionIndex) {
          sessionLabel = `Session ${sessionIndex}`
        }

        return formatQuestSowDescription(sessionLabel)
      }

      // For toBeScheduled sessions, check if there are multiple schedulingConditions
      if (
        Array.isArray(row?.schedulingConditions) &&
        row.schedulingConditions.length > 0
      ) {
        // Get all sessionIndexes and convert to labels
        const sessionLabels = row.schedulingConditions
          .map((condition) => {
            const sessionIndex = condition?.sessionIndex
            if (sessionIndex === 1) {
              return 'Preparation Session'
            } else if (sessionIndex === 2) {
              return ' Integration Session'
            } else if (sessionIndex === 3) {
              return 'Experience Session'
            } else if (sessionIndex) {
              return `Session ${sessionIndex}`
            }
            return null
          })
          .filter((label) => label !== null)
          .sort((a, b) => {
            // Sort: Preparation Session first, then  Integration Session, then Session numbers
            if (a === 'Preparation Session') return -1
            if (b === 'Preparation Session') return 1
            if (a === ' Integration Session') return -1
            if (b === ' Integration Session') return 1
            if (a === 'Experience Session')
              return b === 'Experience Session' ? 0 : -1
            if (b === 'Experience Session') return 1
            // Extract number from "Session X" for sorting
            const numA = parseInt(a.replace('Session ', ''), 10) || 0
            const numB = parseInt(b.replace('Session ', ''), 10) || 0
            return numA - numB
          })

        if (sessionLabels.length > 0) {
          const combinedLabel = sessionLabels.join(' and ')
          return formatQuestSowDescription(combinedLabel)
        }
      }

      // Fallback: try direct sessionIndex
      const sessionIndex = row?.sessionIndex
      let sessionLabel = ''
      if (sessionIndex === 1) {
        sessionLabel = 'Preparation Session'
      } else if (sessionIndex === 2) {
        sessionLabel = ' Integration Session'
      } else if (sessionIndex === 3) {
        sessionLabel = 'Experience Session'
      } else if (sessionIndex) {
        sessionLabel = `Session ${sessionIndex}`
      } else {
        sessionLabel = 'Preparation and Integration Session'
      }

      return formatQuestSowDescription(sessionLabel)
    }

    // Check if this is a scheduled session (has slotStart or scheduledAt)
    const isScheduled = !!(row?.slotStart || row?.scheduledAt)
    const isGrowth = row?.isGrowthEvent
    if (isGrowth) {
      return `Growth Session: ${coachName} & ${clientName}`
    } else if (isScheduled) {
      return `Coaching Session: ${coachName} & ${clientName}`
    }

    return `Coaching Plan: ${coachName} & ${clientName}`
  }

  const getTimeValue = (row) => {
    if (row?.timeLabel) {
      return row.timeLabel
    }
    if (row?.slotStart) {
      // Convert UTC timestamp to user's timezone for display
      const dateTime = moment.utc(row.slotStart).tz(userTimezone)
      const date = dateTime.format('MM/DD/YYYY')
      const time = dateTime.format('LT')
      return `${date} ${time}`
    }
    if (row?.scheduledAt) {
      // Convert UTC timestamp to user's timezone for display
      const dateTime = moment.utc(row.scheduledAt).tz(userTimezone)
      const date = dateTime.format('MM/DD/YYYY')
      const time = dateTime.format('LT')
      return `${date} ${time}`
    }
    return row?.time || '-'
  }

  const getDurationLabel = (row) => {
    if (row?.durationLabel) return row.durationLabel
    if (row?.duration) {
      return `${row.duration} Minutes`
    }
    return '-'
  }

  // Check if session is within 24 hours (applies to all session types)
  const isWithin24Hours = (row) => {
    if (!row?.slotStart) {
      return false
    }

    // const sowType = row?.sowType || row?.sow?.sowType
    // const isConditionalSchedulingEnabled =
    //   row?.isConditionalSchedulingEnabled || false

    // // Only check for QUEST sessions with conditional scheduling enabled
    // if (sowType !== 'QUEST' || !isConditionalSchedulingEnabled) {
    //   return false
    // }
    const sessionStart = moment.utc(row.slotStart)
    const now = moment.utc()
    const hoursUntilSession = sessionStart.diff(now, 'hours', true)

    // Return true if session is within 24 hours (less than 24 hours away)
    return hoursUntilSession > 0 && hoursUntilSession <= 24
  }

  // Render actions for scheduled sessions
  const renderScheduledSessionActions = (row, index) => {
    const within24Hours = isWithin24Hours(row)
    const isRescheduleDisabled =
      row?.isScheduleEnabled === false || within24Hours

    const renderRescheduleButton = () => {
      if (isRescheduleDisabled) {
        return (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip
                style={{
                  '--bs-tooltip-max-width': '22rem',
                  maxWidth: '22rem',
                }}
                id={`tooltip-reschedule-${index}`}
                className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                  }}>
                  {within24Hours
                    ? 'You cannot reschedule or cancel this session within 24 hours of its start time.'
                    : 'Your coach will reach out to you.'}
                </div>
              </Tooltip>
            }>
            <span className='d-inline-block'>
              <div
                className='text-muted pe-2'
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}>
                Reschedule
              </div>
            </span>
          </OverlayTrigger>
        )
      }

      return (
        <div
          className='pe-2 text-saffron-700 hover cursor-pointer'
          onClick={() => handleReschedule(row)}
          style={{ cursor: 'pointer' }}>
          Reschedule
        </div>
      )
    }

    const renderCancelButton = () => {
      if (within24Hours) {
        return (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip
                style={{
                  '--bs-tooltip-max-width': '22rem',
                  maxWidth: '22rem',
                }}
                id={`tooltip-cancel-${index}`}
                className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                  }}>
                  You cannot reschedule or cancel this session within 24 hours
                  of its start time.
                </div>
              </Tooltip>
            }>
            <span className='d-inline-block'>
              <div
                className='text-danger'
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}>
                Cancel
              </div>
            </span>
          </OverlayTrigger>
        )
      }

      return (
        <div
          className='text-danger hover cursor-pointer'
          onClick={() => openCancelModal(row)}
          style={{ cursor: 'pointer' }}>
          Cancel
        </div>
      )
    }

    return (
      <span className='d-flex justify-content-center align-items-center'>
        {renderRescheduleButton()}
        {renderCancelButton()}
      </span>
    )
  }

  // Filter coachDetails to only include remaining sessions from schedulingConditions
  const filteredCoachDetails = useMemo(() => {
    if (!selectedRowForCalendar) return null

    // Get only the schedulingConditions that are in the toBeScheduled row
    const remainingSchedulingConditions =
      selectedRowForCalendar.schedulingConditions || []

    // Create a filtered coachDetails object with only remaining sessions
    return {
      ...selectedRowForCalendar,
      schedulingConditions: remainingSchedulingConditions,
      // Update totalSessions to reflect only remaining sessions
      totalSessions: remainingSchedulingConditions.length,
      remainingSessions: remainingSchedulingConditions.length,
      // Add flag to indicate this is from coaching route (not quest)
      isFromQuest: false,
    }
  }, [selectedRowForCalendar])

  // Notify parent when calendar view changes
  useEffect(() => {
    if (onCalendarViewChange) {
      onCalendarViewChange(!!selectedRowForCalendar)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowForCalendar])

  // Show calendar for a specific row when clicked (for conditional flow)
  if (selectedRowForCalendar) {
    return (
      <>
        <PageHeader
          title='Sessions'
          version='small'
          backArrowAction={() => setSelectedRowForCalendar(null)}
          showBackArrow={true}
        />
        <div className='mt-32px'>
          <QuestSessionSchedulingCalendar
            onScheduleSuccess={handleScheduleSuccess}
            coachDetails={filteredCoachDetails}
          />
        </div>
      </>
    )
  }

  return (
    <div className='mt-32px'>
      <section className='mb-4'>
        <h3 className='s3 mb-3'>To Be Scheduled</h3>
        <div className='table-responsive'>
          {isLoading ? (
            <ShimmerTable col={4} row={4} />
          ) : (
            <Table className='custom-table align-middle'>
              <thead>
                <tr className='s10a border-dark'>
                  {/* <th width='5%'>#</th> */}
                  <th width='70%' className='text-start'>
                    DESCRIPTION
                  </th>
                  <th width='25%' className='text-center'>
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {!toBeScheduledSessions.length &&
                  renderEmptyRow(3, 'No sessions to schedule.')}
                {toBeScheduledSessions.map((row, index) => {
                  const hasFirstSessionScheduled = isFirstSessionScheduled(row)
                  return (
                    <tr key={`${row?.coachId || row?.coachName}-${index}`}>
                      {/* <td width='5%'>{index + 1}</td> */}
                      <td width='75%' className='text-start'>
                        {getDescription(row)}
                      </td>
                      <td width='25%' className='text-center'>
                        {hasFirstSessionScheduled ? (
                          row?.isScheduleEnabled === false ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip
                                  style={{
                                    '--bs-tooltip-max-width': '22rem',
                                    maxWidth: '22rem',
                                  }}
                                  id={`tooltip-schedule-remaining-${index}`}
                                  className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                                  <div
                                    style={{
                                      width: '100%',
                                      maxWidth: '100%',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    Your coach will reach out to you.
                                  </div>
                                </Tooltip>
                              }>
                              <div
                                className='text-muted'
                                style={{
                                  cursor: 'not-allowed',
                                  opacity: 0.6,
                                }}>
                                {row?.remainingSessions > 0
                                  ? 'Schedule More'
                                  : ''}
                              </div>
                            </OverlayTrigger>
                          ) : (
                            <div
                              className='text-saffron-700 hover cursor-pointer'
                              onClick={() => handleScheduleRemaining(row)}>
                              {row?.remainingSessions > 0
                                ? 'Schedule More'
                                : ''}
                            </div>
                          )
                        ) : row?.isScheduleEnabled === false ? (
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <Tooltip
                                style={{
                                  '--bs-tooltip-max-width': '22rem',
                                  maxWidth: '22rem',
                                }}
                                id={`tooltip-schedule-${index}`}
                                className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info
'>
                                <div
                                  style={{
                                    width: '100%',
                                    maxWidth: '100%',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  Your coach will reach out to you.
                                </div>
                              </Tooltip>
                            }>
                            <div
                              className='text-muted'
                              style={{
                                cursor: 'not-allowed',
                                opacity: 0.6,
                              }}>
                              Schedule
                            </div>
                          </OverlayTrigger>
                        ) : (
                          <div
                            className='text-saffron-700 hover cursor-pointer'
                            onClick={() => openScheduleModal(row)}
                            style={{ cursor: 'pointer' }}>
                            Schedule
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </div>
      </section>
      <section>
        <h3 className='s3 mb-3'>Scheduled</h3>
        <div className='table-responsive'>
          {isLoading ? (
            <ShimmerTable col={4} row={4} />
          ) : (
            <Table className='custom-table align-middle'>
              <thead>
                <tr className='s10a border-dark'>
                  {/* <th width='7%'>#</th> */}
                  <th width='35%' className='text-start'>
                    DESCRIPTION
                  </th>
                  <th width='30%' className='text-center'>
                    DATE & TIME
                  </th>
                  <th width='10%' className='text-center'>
                    DURATION
                  </th>
                  <th width='25%' className='text-center'>
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {!scheduledSessions.length &&
                  renderEmptyRow(5, 'No scheduled sessions.')}
                {scheduledSessions.map((row, index) => {
                  return (
                    <tr
                      key={`${
                        row?.eventId || row?._id || row?.sessionId
                      }-${index}`}>
                      {/* <td width='7%'>{index + 1}</td> */}
                      <td width='35%' className='text-start'>
                        {getDescription(row)}
                      </td>
                      <td width='30%' className='text-center'>
                        {getTimeValue(row)}
                      </td>
                      <td width='10%' className='text-center'>
                        {getDurationLabel(row)}
                      </td>
                      <td width='25%' className='text-center'>
                        {renderScheduledSessionActions(row, index)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </div>
      </section>
      <ScheduleGuidelineModal
        show={showScheduleGuideline}
        handleClose={closeScheduleModal}
        coachDetails={selectedCoachRow}
        isFirstSessionScheduled={isFirstSessionScheduled(selectedCoachRow)}
      />
      <ConditionalScheduleGuidelineModal
        show={showConditionalScheduleGuideline}
        handleClose={closeConditionalScheduleModal}
        coachDetails={selectedCoachRowForConditional}
        onScheduleAll={handleConditionalScheduleAll}
      />
      <CancelSessionModal
        show={showCancelModal}
        onClose={closeCancelModal}
        onConfirm={handleCancelConfirm}
        coachName={getFullName(sessionPendingCancel?.coach)}
        sessionTime={
          sessionPendingCancel ? getTimeValue(sessionPendingCancel) : ''
        }
        isSubmitting={isCancelling}
      />
    </div>
  )
}

export default UpcomingSessions
